import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useCheckoutPageProps from "@hooks/useCheckoutPageProps.ts";
import useCheckoutQueryParams from "@hooks/useCheckoutQueryParams";
import usePostCheckoutState from "@hooks/usePostCheckoutState";

import { CheckoutContext } from "@/providers/context/CheckoutContext";
import { useAtom } from "jotai";

import {
    Box,
    Grid,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs
} from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";

import { checkoutAtom, pageQueryParamsAtom } from "@atoms/checkoutAtom.ts";

import ContentLayout from "@layouts/ContentLayout";

import { Skeleton } from "@components/Skeleton";
import { Text } from "@components/Text";

const GradeCurriculumContainer = () => {
    const { subjectsByGrade } = useCheckoutPageProps();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [checkout] = useAtom(checkoutAtom);
    const checkoutContext = useContext(CheckoutContext);

    const { isProductFamiliesLoading } = checkoutContext;

    const [pageQueryParams] = useAtom(pageQueryParamsAtom);

    const [pathNavigateTo] = useState(CheckoutPageRoutesEnum.PLAN_SELECT_PAGE);

    const { routeWithQueryParams, onSetupQueryParams } =
        useCheckoutQueryParams();
    const { checkoutStateMutationState, checkoutStateMutate } =
        usePostCheckoutState(() => {
            navigate(routeWithQueryParams(pathNavigateTo));
        });

    const isProductFamilySelected = !!checkout.productFamily;

    const onSaveData = () => {
        checkoutStateMutate();
    };

    const onGoBack = () => {
        navigate(
            routeWithQueryParams(
                CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE
            )
        );
    };

    const grades = subjectsByGrade?.filter(
        grade => grade.grade === checkout.grade?.id
    );

    const subjects = grades?.length !== 0 ? grades[0].subjects : [];

    const grade = grades[0] !== undefined ? grades[0].year : "";

    /**
     * "useEffect" hook to set the query parameters dynamically based on the selected product.
     */
    useEffect(() => {
        if (checkoutStateMutationState.isSuccess) {
            onSetupQueryParams({
                ...pageQueryParams,
                addon: checkout.addon ? checkout.addon.id : null,
                plan: checkout.plan ? checkout.plan.id : null,
                family: checkout.productFamily!.id,
                grade: checkout.grade!.name
            });
        }
    }, [checkout, checkoutStateMutationState]);

    return (
        <ContentLayout
            title={t(
                "checkout.gradeCurriculumPage.title",
                "Here’s what we cover in the Year {{grade}} Program",
                { grade }
            )}
            description={t(
                "checkout.gradeCurriculumPage.desc",
                "Our programs cover the Australian curriculum by blending online learning with practical, hands-on activities"
            )}
            headerBackButton
            footerActionsProps={{
                isNextBtnLoading: checkoutStateMutationState.isPending,
                canGoNext: isProductFamilySelected,
                onGoNext: onSaveData,
                onGoBack,
                btnClassHierarchy: {
                    next: [
                        ClassesEnum.GradeCurriculumContainer,
                        ClassesEnum.Button
                    ]
                },
                btnActions: {
                    next: "goNextFromProductFamilySelectPage"
                }
            }}
            step={2}
            containerWidth={["95%", null, null, null, null]}
            className={ClassesEnum.GradeCurriculumContainer}
        >
            <Tabs
                height="365px"
                background="gray.100"
                borderRadius="16px"
                border="1px solid"
                borderColor="gray.400"
            >
                <Grid templateColumns="4fr 8fr" gap={4} height="100%">
                    <Box height="100%" overflow="hidden">
                        <TabList
                            display="flex"
                            flexDirection="column"
                            background="var(--euka-colors-white-500)"
                            borderRadius="16px 0px 0px 16px"
                            padding="18px 0px"
                            height="100%"
                            overflowY="auto"
                            borderRight="1px solid"
                            borderColor="gray.400"
                            borderBottom="none"
                        >
                            {subjects?.map(subject => (
                                <Tab
                                    key={subject.id}
                                    display="block"
                                    textAlign="left"
                                    padding="6px 24px"
                                    _selected={{
                                        background: "gray.100"
                                    }}
                                >
                                    <Text fontSize={14} type="subHeading">
                                        {subject.name}
                                    </Text>
                                </Tab>
                            ))}
                        </TabList>
                    </Box>
                    <TabPanels>
                        {subjects?.map(subject => (
                            <TabPanel key={subject.id}>
                                <Text fontSize={18} mb="12px" type="subHeading">
                                    {subject.name}
                                </Text>
                                <Text
                                    type="normal"
                                    data-testid={`subject-content-${subject.id}`}
                                >
                                    {subject.pivot?.description ??
                                        subject.description}
                                </Text>
                            </TabPanel>
                        ))}
                    </TabPanels>
                </Grid>
            </Tabs>
            {isProductFamiliesLoading && <Skeleton />}
        </ContentLayout>
    );
};

export default GradeCurriculumContainer;
